import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { TableHeader } from "../datatable/TableHeader";
import { FilterMatchMode } from "primereact/api";
import { Column } from "primereact/column";
import { FormSkeleton } from "../skeleton/FormSkeleton";
import { Actions } from "../datatable/Actions";
import { DialogProps } from "../dialog/CustomDialog";
import {
  MultiContentDialog,
  MultiContentDialogProps,
} from "../dialog/MultiContentDialog";
import { EnvCreator } from "../environments";
import { useEnvironments } from "../../hooks/useEnvironments";
import { useAgents } from "../../hooks/useAgents";
import { Environment } from "../../interfaces";
import { AppContext } from "../../context/index";
import { Tag } from "iuvity-ui-components/dist/tag";
import { capitalizeFirstLetter } from "../../utils";
import { EmptyTemplate } from "../datatable/EmptyTemplate";
import noResultsImage from "../../assets/Illustration_NoResults.svg";
import { Button, Calendar } from "iuvity-ui-components";
import { Controller, useForm } from "react-hook-form";
import { Dropdown } from 'primereact/dropdown';
import { REVALIDATE_FORM_PROPS } from "../../constants";



interface AppDataTableActionsProps {
  options: any;
  auxModalController: React.Dispatch<React.SetStateAction<DialogProps>>;
}

type EnvActionTypes = "CREATE" | "UPDATE" | "CLONE" | "IMPORT" | "EXPORT" | "DELETE-CACHE";

export const AppDataTableActions: React.FC<AppDataTableActionsProps> = ({
  options,
  auxModalController,
}) => {
  const {
    t,
    appState: { selectedApplication, pageFilter, language },
    setAppState,
  } = useContext(AppContext);

  const navigate = useNavigate();
  const { data: envData, envAction, reloadFn } = useEnvironments("DATA");
  const { data: agentData } = useAgents("DATA");

  let order = ['ACTIVE', 'DRAFT', 'INACTIVE', 'DELETED'];

  const columns = useMemo(() => {
    return [
      {
        field: "name",
        header: t("Environments.nameTableLabel"),
      },
      {
        field: "agentname",
        header: t("Environments.agentnameLabel"),
      },
      {
        field: "status",
        header: t("Environments.statusLabel"),
      },
      {
        field: "cacheurl",
        header: t("Environments.cacheurlLabel"),
      },
      {
        field: "cacheapikey",
        header: t("Environments.cacheapikeyLabel"),
      },
      {
        field: "actions",
        header: t("Environments.actionsLabel"),
      },
    ];
  }, [t]);

  const formOptions = useMemo(() => {
    if (envData && agentData) {
      return {
        agents: agentData.filter((agent: any) => (
          agent.status !== 'DELETED' &&
          agent.status !== 'DRAFT'
        )).map((agent: any) => ({
          label: agent?.name,
          value: agent?.id,
        })),
        environments: envData
          .map((env: any) => ({
            label: env?.name,
            value: env?.name,
            tenantid: env?.tenantid,
          }))
          .reduce(function (result: any, element: any) {
            if (
              ![...new Set(result.map((env: any) => env.value))].includes(
                element.value
              )
            ) {
              result.push(element);
            }
            return result;
          }, []),
      };
    } else {
      return;
    }
  }, [envData, agentData]);

  const [mcModalProps, setMcModalProps] = useState<MultiContentDialogProps>({
    children: null,
  } as MultiContentDialogProps);

  const [selectedColumns, setSelectedColumns] = useState(columns);
  const filterDefaultValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: pageFilter?.status, matchMode: FilterMatchMode.EQUALS },
    createdat: { value: pageFilter?.createdat, matchMode: FilterMatchMode.CONTAINS },
  };
  const formBlankValues = {
    status: ""
  };
  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: formBlankValues,
  });
  const { control, setValue, getValues } = form;

  const [filters, setFilters] = useState(filterDefaultValues);

  const onClearFilters = () => {
    setFilters(filterDefaultValues);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(formBlankValues);

  };
  const onEdit = (env: any) => {
    setAppState((prev) => ({
      ...prev,
      selectedEnvironment: env,
    }));
  };

  const actionDeleteCacheFn = async (env: any) => {
    await envAction("DELETE-CACHE", env.id);
  };

  const onDeleteCache = (env: any) => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: t("Environments.deleteCacheModalTitle"),
      content: t("Environments.deleteCacheModalContent1"),
      onConfirm: () => actionDeleteCacheFn(env),
    }));
  };

  const handleModalProps = async (type: EnvActionTypes, env?: Environment) => {
    switch (type) {
      case "CREATE":
        return (() => {
          document.body.style.overflowY = "hidden";
          setMcModalProps((prev) => ({
            ...prev,
            children: (
              <EnvCreator
                options={formOptions?.agents}
                update={false}
                createFn={envAction}
                reloadFn={reloadFn}
                modalController={setMcModalProps}
                auxModalController={auxModalController}
              />
            ),
          }))
        })();
      case "UPDATE":
        onEdit(env);
        return (() => {
          document.body.style.overflowY = "hidden";
          setMcModalProps((prev) => ({
            ...prev,
            children: (
              <EnvCreator
                options={formOptions?.agents}
                update={true}
                createFn={envAction}
                reloadFn={reloadFn}
                modalController={setMcModalProps}
                auxModalController={auxModalController}
                env={env}
              />
            ),
          }))
        })();
      case "IMPORT":
        setAppState((prev) => ({
          ...prev,
          selectedEnvironment: {
            ...env!,
            fullname: `${options.tenants.find(
              (tenant: any) => tenant.value === env?.tenantid
            )?.label
              }_${options.applications.find(
                (application: any) => application.value === env?.applicationid
              )?.label
              }_${env?.name}`,
          },
        }));
        return navigate("/applications/edit/import");
      case "EXPORT":
        setAppState((prev) => ({
          ...prev,
          selectedEnvironment: {
            ...env!,
            fullname: `${options.tenants.find(
              (tenant: any) => tenant.value === env?.tenantid
            )?.label
              }_${options.applications.find(
                (application: any) => application.value === env?.applicationid
              )?.label
              }_${env?.name}`,
          },
        }));
        return navigate("/applications/edit/export");
      case "DELETE-CACHE":
        onDeleteCache(env);
        return;

      default:
        return;
    }
  };
  const statusBodyTemplate = (rowData: any) => (
    <Tag
      value={capitalizeFirstLetter(rowData.status)}
      severity={
        (['active', 'activo', 'activa'].includes(rowData.status.toLowerCase()) && 'success') ||
        (['draft', 'borrador'].includes(rowData.status.toLowerCase()) && 'info') ||
        (['delete', 'eliminado'].includes(rowData.status.toLowerCase()) && 'danger') ||
        'warning'
      }
    />
  );
  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        label={t("Environments.applyFilterLabel")}
        className="p-button"
        onClick={() => {
          options.filterApplyCallback();
          setAppState((prev: any) => ({
            ...prev,
            pageFilter: getValues(),
          }));
        }}
      />
    )
  };
  const statusItemTemplate = (option: any) => {
    return <span>{option}</span>;
  }
  const statuses = [
    `${t("Environments.activeStatusLabel")}`,
    `${t("Environments.inactiveStatusLabel")}`,
    `${t("Environments.draftStatusLabel")}`,
  ];
  const statusFilterTemplate = (options: any) => {
    return (
      <Controller
        name={options.field}
        control={control}
        render={({ field }) => {
          field.onChange = (e) => {
            setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
            options.filterCallback(e.value);
          };
          return (
            <>
              <label htmlFor={options.field} className="block lb mt-2 mb-3">
                {t(`Environments.${options.field}Label`)}
              </label>
              {/* // @ts-ignore */}
              <Dropdown
                options={statuses}
                itemTemplate={statusItemTemplate}
                placeholder={t(`Environments.${options.field}FilterPlaceholder`)}
                className="p-column-filter mb-2"
                showClear={false}
                {...field}
              />
            </>
          );
        }}
      />

    )
  }

  const filterClearTemplate = (options: any) => {
    return (
      <Button
        label={t("Environments.clearFilterLabel")}
        variant="p-button-text"
        onClick={() => { onClearFilters(); }}
      />
    )
  };


  const filterTemplate = (options: any) => {
    if (options.field == 'status') return statusFilterTemplate(options);
    return ''
  }

  useEffect(() => {
    setFilters(filterDefaultValues);
  }, [pageFilter]);

  useEffect(() => {
    onClearFilters()
  }, []);

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`Environments.${
            col.field === 'name'
              ? 'nameTable'
              : col.field
          }Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  return (
    <div className="section_edit_app_env">
      <SubheaderActions
        className="px-0 mx-2 pt-3 wrapper-environment-button"
        style={{ paddingBottom: '1rem !important' }}
        actions={[
          {
            id: "111",
            icon: "iu iu-icon-plus",
            label: t("Environments.addEnvironmentLabel"),
            onClick: () => handleModalProps("CREATE"),
          },
        ]}
      />

      <BoxFull className=" mdt-c mt-0 p-3">
        {formOptions ? (
          <DataTable
            header={
              <TableHeader
                title={t("Environments.environmentsTitle")}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={filters}
                setFilters={setFilters}
                reload={reloadFn}
                onClearFiltersColumn={onClearFilters}
              />
            }
            sortField="order"
            sortOrder={1}
            emptyMessage={<EmptyTemplate image={noResultsImage} text={t('Environments.emptyDataTableMessage')} />}
            rows={10}
            size="normal"
            filters={filters}
            value={envData
              .sort((a: any, b: any) => {
                return order.indexOf(a.status) - order.indexOf(b.status);
              })
              .filter(
                (env: any) => env?.applicationid === selectedApplication?.id
              )
              .map((env: any) => ({
                ...env,
                agentname: env?.agent?.status !== 'DELETED' ? env?.agent?.name : "",
                status: t(`Environments.${env.status.toLowerCase()}StatusLabel`),
                actions: (
                  <Actions
                    onEdit={() => handleModalProps("UPDATE", env)}
                    viewButtonDelete={false}
                    auxButtons={(() => {
                      const auxButtonsList = [

                        {
                          label: t("Applications.importBtnLabel"),
                          icon: "iu-icon-upload",
                          function: () => handleModalProps("IMPORT", env),
                          color: "var(--warningColor)",
                        },
                        {
                          label: t("Applications.exportBtnLabel"),
                          icon: "iu-icon-download",
                          function: () => handleModalProps("EXPORT", env),
                          color: "var(--mainColor)",
                        },
                      ]
                      return (!!env?.cacheurl && !!env?.cacheapikey)
                        ? [
                          ...auxButtonsList,
                          {
                            label: t("Applications.deleteCacheBtnLabel"),
                            icon: "iu-icon-restricted",
                            function: () => handleModalProps("DELETE-CACHE", env),
                            color: "var(--errorColor)",
                          },
                        ]
                        : auxButtonsList
                    })()}
                  />
                ),
              }))}
            scrollable
            resizableColumns
            globalFilterFields={[
              "status",
              'agentname',
              "name",
              "cacheurl",
              "cacheapikey"
            ]}
            filterDisplay="menu"
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
          >
            {selectedColumns.map((col, idx) => {
              return (
                <Column
                  key={`${col.field}-${idx}`}
                  field={col.field}
                  header={col.header}
                  sortable={false}
                  align={col.field === "actions" ? "center" : "left"}

                  headerStyle={col.field === "actions"
                    ? { justifyContent: "center" }
                    : undefined}
                  style={{
                    boxShadow: (col.field === "actions")
                      ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                      : undefined,
                    whiteSpace: "normal",
                  }}
                  body={(rowData) => {
                    if (col.field == 'status') {
                      return statusBodyTemplate(rowData)
                    }
                    return rowData[col.field]
                  }}
                  frozen={col.field === "actions" ? true : false}
                  alignFrozen={col.field === "actions" ? "right" : undefined}
                  filterField={col.field}
                  filter={col.field == 'status'}
                  showFilterMatchModes={false}
                  filterMenuStyle={{ width: "23rem" }}
                  filterApply={filterApplyTemplate}
                  filterClear={filterClearTemplate}
                  filterElement={filterTemplate}
                />
              );
            })}
          </DataTable>
        ) : (
          <FormSkeleton length={3} />
        )}
      </BoxFull>

      <MultiContentDialog children={mcModalProps.children} />
    </div>
  );
};
